import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  input,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { ButtonComponent } from '../../../buttons/button.component';
import { IconComponent } from '../../../icons/icon.component';
import { TourItinerary } from '../../models/tour-itinerary';
import { TourService } from '../../tour.service';

/**
 * Component for displaying tour progress and allowing navigation between itineraries.
 *
 * The TourProgressComponent provides a UI card that shows the current tour progress,
 * lists available itineraries, and allows users to start or resume tour steps.
 * It appears as a floating, collapsible card when the tour is active and the
 * showTourProgressCard signal is set to true.
 *
 * The component automatically filters itineraries to only show those that have
 * showNameInTourProgress set to true, giving tour designers control over which
 * itineraries appear in the progress UI.
 *
 * @example
 * ```html
 * <!-- Include in your app template -->
 * <mop-tour-progress></mop-tour-progress>
 *
 * <!-- The component will automatically show/hide based on tour state -->
 * ```
 *
 * @example
 * ```typescript
 * // Control visibility in your tour configuration
 * const tourStop = new TourStopPoi('#element', 'Title', 'Description', {
 *   nextButton: {
 *     displayText: 'Continue',
 *     listener: () => {
 *       tourService.showTourProgressCard.set(true); // Show progress card
 *       tourService.nextStop();
 *     }
 *   }
 * });
 * ```
 */
@Component({
  selector: 'mop-tour-progress',
  templateUrl: './tour-progress.component.html',
  styleUrl: './tour-progress.component.scss',
  host: {
    class: 'card shadow-lg',
    '[class.onboarding-shifted]': 'onboardingShifted()',
    '[style.overflow]': 'isCollapsed() ? "visible !important" : ""',
  },
  imports: [IconComponent, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourProgressComponent {
  /** The tour service instance for managing tour state */
  private readonly tourService = inject(TourService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  tourComplete = this.tourService.tourComplete;

  /**
   * Input to track if the onboarding should be shifted.
   * When true, the tour progress button will be positioned to the left.
   */
  onboardingShifted = input<boolean>(false);

  /**
   * Controls the collapsed/expanded state of the tour progress card.
   *
   * When true, the main body of the card is hidden, and it appears as a smaller
   * "Help" button. When false, the full checklist is visible. This state is
   * toggled by the user clicking the expand/collapse button.
   */
  isCollapsed = signal(false);

  /**
   * Host-binds the `collapsed` class to the component's host element.
   *
   * This allows the component to be styled differently via the `:host.collapsed`
   * selector when it is in a collapsed state, enabling transitions and hiding
   * the main card shadow and background.
   */
  @HostBinding('class.collapsed')
  get isHostCollapsed() {
    return this.isCollapsed();
  }

  /**
   * Computed signal that determines if the progress card should be visible.
   *
   * The card is shown when both the tour is started and the showTourProgressCard
   * signal is set to true. This allows for fine-grained control over when the
   * progress card appears during the tour.
   *
   * @returns True if the progress card should be displayed, false otherwise
   */
  show = computed(
    () =>
      this.tourService.tourStarted() && this.tourService.showTourProgressCard()
  );

  /**
   * Computed signal containing itineraries that should be displayed in the progress UI.
   *
   * This filters the complete list of itineraries to only include those that have
   * showNameInTourProgress set to true. This allows tour designers to control which
   * itineraries appear in the progress card while keeping others hidden.
   *
   * @returns Array of itineraries to display in the progress UI
   */
  itineraries = computed(() =>
    this.tourService
      .itineraries()
      .filter((itinerary) => itinerary.showNameInTourProgress)
  );

  /**
   * Starts or resumes an itinerary.
   *
   * This method finds the index of the specified itinerary in the complete
   * list and calls the tour service to navigate to it. This allows users
   * to start a new step or return to one they may have skipped earlier.
   *
   * @param skippedItinerary - The itinerary to start or resume
   *
   * @example
   * ```typescript
   * // Called from template when user clicks to start an itinerary or resume a skipped itinerary
   * startItinerary(itinerary);
   * // The tour will return to the beginning of the specified itinerary
   * ```
   */
  startItinerary(skippedItinerary: TourItinerary) {
    const index = this.tourService
      .itineraries()
      .findIndex((itinerary) => itinerary.name === skippedItinerary.name);

    if (index > -1) {
      this.tourService.gotoItinerary(index);
    }
  }

  /**
   * Ends the tour completely.
   *
   * This method is called by the "Dismiss" button in the template. It tells the
   * tour service to end the current tour, which will hide the tour progress
   * card and any active tour popovers.
   *
   * @example
   * ```html
   * <mop-button text="Dismiss" (click)="endTour()"></mop-button>
   * ```
   */
  dismissTour() {
    this.tourService.endTour();
    this.tourService.logBrazeCustomEvent('onboardingtour_dismissed');
  }

  endTour() {
    const endOfTour = this.tourService.itineraries().length - 1;
    this.tourService.gotoItinerary(endOfTour);
  }

  toggleCollapsed() {
    this.isCollapsed.set(!this.isCollapsed());

    const contentElement = (
      this.elementRef.nativeElement as HTMLElement
    ).querySelector('.tour-progress-content');

    if (contentElement) {
      const handleTransitionEnd = () => {
        this.tourService.highlightResizedTourProgressComponent();
        contentElement.removeEventListener(
          'transitionend',
          handleTransitionEnd
        );
      };

      contentElement.addEventListener('transitionend', handleTransitionEnd);
    }
  }
}
